<template>
  <div
    class="table-shimmer"
    :style="shimmerStyle"
    role="status"
    aria-label="Loading table"
  >
    <div class="table-shimmer__viewport">
      <div class="table-shimmer__table">
        <div
          v-if="showHeader"
          class="table-shimmer__row table-shimmer__row--header"
          aria-hidden="true"
        >
          <div
            v-for="column in columnItems"
            :key="`header-${column}`"
            class="table-shimmer__cell"
          >
            <span
              class="table-shimmer__line table-shimmer__line--header"
              :style="{ width: headerLineWidth(column) }"
            ></span>
          </div>
        </div>

        <div
          v-for="row in rowItems"
          :key="`row-${row}`"
          class="table-shimmer__row"
          aria-hidden="true"
        >
          <div
            v-for="column in columnItems"
            :key="`cell-${row}-${column}`"
            class="table-shimmer__cell"
          >
            <div v-if="column === 1" class="table-shimmer__identity">
              <span class="table-shimmer__avatar"></span>
              <span class="table-shimmer__identity-lines">
                <span class="table-shimmer__line table-shimmer__line--title"></span>
                <span class="table-shimmer__line table-shimmer__line--meta"></span>
              </span>
            </div>
            <span
              v-else-if="column === statusColumn"
              class="table-shimmer__pill"
            ></span>
            <span
              v-else-if="column === normalizedColumns"
              class="table-shimmer__actions"
            ></span>
            <span
              v-else
              class="table-shimmer__line"
              :style="{ width: bodyLineWidth(row, column) }"
            ></span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TableShimmer',
  props: {
    rows: {
      type: Number,
      default: 6,
    },
    columns: {
      type: Number,
      default: 6,
    },
    minWidth: {
      type: Number,
      default: 720,
    },
    showHeader: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    normalizedRows() {
      return Math.max(1, Math.floor(Number(this.rows) || 6));
    },
    normalizedColumns() {
      return Math.max(1, Math.floor(Number(this.columns) || 6));
    },
    rowItems() {
      return Array.from({ length: this.normalizedRows }, (_, index) => index + 1);
    },
    columnItems() {
      return Array.from({ length: this.normalizedColumns }, (_, index) => index + 1);
    },
    statusColumn() {
      if (this.normalizedColumns < 3) return 0;
      return Math.max(2, Math.ceil(this.normalizedColumns / 2));
    },
    columnTemplate() {
      if (this.normalizedColumns === 1) {
        return 'minmax(180px, 1fr)';
      }

      if (this.normalizedColumns === 2) {
        return 'minmax(180px, 1.4fr) minmax(90px, 0.65fr)';
      }

      return `minmax(180px, 1.35fr) repeat(${this.normalizedColumns - 2}, minmax(118px, 1fr)) minmax(82px, 0.55fr)`;
    },
    resolvedMinWidth() {
      const calculated = this.normalizedColumns * 126;
      return Math.max(Number(this.minWidth) || 720, calculated);
    },
    shimmerStyle() {
      return {
        '--table-shimmer-columns': this.columnTemplate,
        '--table-shimmer-min-width': `${this.resolvedMinWidth}px`,
      };
    },
  },
  methods: {
    headerLineWidth(column) {
      const widths = ['54%', '42%', '58%', '48%', '52%', '38%'];
      return widths[(column - 1) % widths.length];
    },
    bodyLineWidth(row, column) {
      const widths = ['68%', '52%', '74%', '46%', '60%', '56%'];
      return widths[(row + column - 2) % widths.length];
    },
  },
};
</script>

<style scoped>
.table-shimmer {
  width: 100%;
}

.table-shimmer__viewport {
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  border: 1px solid #e4e7ec;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.03);
}

.table-shimmer__table {
  min-width: var(--table-shimmer-min-width);
}

.table-shimmer__row {
  display: grid;
  grid-template-columns: var(--table-shimmer-columns);
  min-height: 58px;
  border-top: 1px solid #edf0f4;
}

.table-shimmer__row:first-child {
  border-top: 0;
}

.table-shimmer__row--header {
  min-height: 44px;
  background: #f5f7fa;
}

.table-shimmer__cell {
  min-width: 0;
  display: flex;
  align-items: center;
  padding: 0 18px;
}

.table-shimmer__identity {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
}

.table-shimmer__identity-lines {
  min-width: 0;
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 8px;
}

.table-shimmer__avatar,
.table-shimmer__line,
.table-shimmer__pill,
.table-shimmer__actions {
  position: relative;
  overflow: hidden;
  background: #eef1f6;
}

.table-shimmer__avatar::after,
.table-shimmer__line::after,
.table-shimmer__pill::after,
.table-shimmer__actions::after {
  content: "";
  position: absolute;
  inset: 0;
  transform: translateX(-100%);
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.78) 50%,
    rgba(255, 255, 255, 0) 100%
  );
  animation: table-shimmer-sweep 1.25s ease-in-out infinite;
}

.table-shimmer__avatar {
  flex: 0 0 auto;
  width: 32px;
  height: 32px;
  border-radius: 50%;
}

.table-shimmer__line {
  display: block;
  width: 64%;
  height: 10px;
  border-radius: 999px;
}

.table-shimmer__line--header {
  height: 8px;
  background: #e0e5ed;
}

.table-shimmer__line--title {
  width: 72%;
  height: 11px;
}

.table-shimmer__line--meta {
  width: 48%;
  height: 8px;
  background: #f0f2f6;
}

.table-shimmer__pill {
  width: 74px;
  height: 22px;
  border-radius: 999px;
  background: #e8f7ef;
}

.table-shimmer__actions {
  width: 28px;
  height: 28px;
  margin-left: auto;
  border-radius: 8px;
}

.table-shimmer__viewport::-webkit-scrollbar {
  height: 6px;
}

.table-shimmer__viewport::-webkit-scrollbar-thumb {
  border-radius: 999px;
  background: #d9dee7;
}

@keyframes table-shimmer-sweep {
  100% {
    transform: translateX(100%);
  }
}

@media (max-width: 575.98px) {
  .table-shimmer__cell {
    padding: 0 14px;
  }

  .table-shimmer__row {
    min-height: 54px;
  }
}
</style>
